import { setOpenPanel, clearOpenPanelRef } from './panelManager.js';

export const PanelMixin = {
    togglePanel() {
        if (this.hasAttribute('disabled')) return;
        this.isOpen ? this.closePanel() : this.openPanel();
    },

    openPanel() {
        if (!this.panel) return;

        const type = this.getAttribute('type') || 'dropdown';

        if (type === 'daterange' && this.rangeStart) {
            const [y, m] = this.rangeStart.split('-').map(Number);
            this.rangeMonth = new Date(y, m - 1, 1);
            this.buildRangePanel();
        }

        setOpenPanel(this);
        this.isOpen = true;
        this.style.zIndex = '200';
        this.panel.hidden = false;
        this.trigger?.classList.add('open');
        this.trigger?.setAttribute('aria-expanded', 'true');
        this._openPortal();
        this.positionPanel();

        requestAnimationFrame(() => {
            switch (type) {
                case 'dropdown': {
                    const searchEl = this.panel?.querySelector('.inputSearchInput');
                    if (searchEl) {
                        searchEl.value = '';
                        this.panel?.querySelectorAll('.inputOption').forEach(i => i.style.display = '');
                        searchEl.focus();
                    } else {
                        const focusTarget = this.panel?.querySelector('.inputOption.selected') || this.panel?.querySelector('.inputOption:not(.disabled)');
                        focusTarget?.focus();
                    }
                    break;
                }
                case 'datepicker':
                    this.focusDateDay();
                    break;
                case 'daterange':
                    this.focusRangeDay();
                    break;
                case 'timepicker': {
                    this.buildTimepickerPanel?.();
                    this.panel?.querySelectorAll('.timeScrollCol').forEach(col => {
                        const sel = col.querySelector('.timeScrollItem.selected');
                        if (!sel) return;
                        col.scrollTop = sel.offsetTop - col.clientHeight / 2 + sel.offsetHeight / 2;
                    });
                    this.panel?.querySelector('.timeScrollItem[tabindex="0"]')?.focus();
                    break;
                }
                case 'timerange': {
                    this.panel?.querySelectorAll('.timeScrollCol').forEach(col => {
                        const sel = col.querySelector('.timeScrollItem.selected');
                        if (!sel) return;
                        col.scrollTop = sel.offsetTop - col.clientHeight / 2 + sel.offsetHeight / 2;
                    });
                    this.panel?.querySelector('.timeScrollItem[tabindex="0"]')?.focus();
                    break;
                }
            }
        });
    },

    closePanel() {
        this.isOpen = false;
        this.style.zIndex = '';
        if (this.panel) {
            this.panel.hidden = true;
            this.panel.style.cssText = '';
            if (this._portal?.shadowRoot.contains(this.panel)) {
                this.shadowRoot.appendChild(this.panel);
            }
        }
        this.trigger?.classList.remove('open');
        this.trigger?.setAttribute('aria-expanded', 'false');
        clearOpenPanelRef(this);
    },

    positionPanel() {
        if (!this.panel || !this.trigger) return;
        const rect     = this.trigger.getBoundingClientRect();
        const panelH   = this.panel.offsetHeight || this.panel.scrollHeight || 300;
        const boundary = this.closest('.dialog')?.getBoundingClientRect();
        const boundaryTop    = boundary ? Math.max(boundary.top, 0) : 0;
        const boundaryBottom = boundary ? Math.min(boundary.bottom, window.innerHeight) : window.innerHeight;
        const below  = boundaryBottom - rect.bottom;
        const above  = rect.top - boundaryTop;

        const isCalendar = this.panel.classList.contains('calPanel');

        this.panel.style.position = 'fixed';
        this.panel.style.minWidth = isCalendar ? '' : `${rect.width}px`;
        this.panel.style.zIndex   = '9999';

        if (isCalendar) {
            const panelW = this.panel.offsetWidth || this.panel.scrollWidth || rect.width;
            this.panel.style.left = `${rect.left + (rect.width - panelW) / 2}px`;
        } else {
            this.panel.style.left = `${rect.left}px`;
        }

        const flip      = below < panelH && above > below;

        if (isCalendar) {
            const available = Math.max((flip ? above : below) - 8, 120);
            this.panel.style.maxHeight = `${Math.min(panelH, available)}px`;
            this.panel.style.overflowY = panelH > available ? 'auto' : '';
        } else {
            this.panel.style.maxHeight = '';
            this.panel.style.overflowY = '';
        }

        if (flip) {
            this.panel.style.top    = '';
            this.panel.style.bottom = `${window.innerHeight - rect.top + 4}px`;
            this.panel.classList.add('flip');
        } else {
            this.panel.style.top    = `${rect.bottom + 4}px`;
            this.panel.style.bottom = '';
            this.panel.classList.remove('flip');
        }
    },

    _openPortal() {
        if (!this.panel) return;
        if (!this._portal) {
            this._portal = document.createElement('div');
            this._portal.attachShadow({ mode: 'open' });
            document.body.appendChild(this._portal);
        }
        this._portal.shadowRoot.adoptedStyleSheets = this.shadowRoot.adoptedStyleSheets;
        this._portal.shadowRoot.appendChild(this.panel);
        this.panel.style.display = 'block';
    },

    _destroyPortal() {
        if (!this._portal) return;
        if (this.panel && this._portal.shadowRoot.contains(this.panel)) {
            this.shadowRoot.appendChild(this.panel);
        }
        this._portal.remove();
        this._portal = null;
    },

    attachOutsideHandler() {
        if (this.outsideHandler) {
            try { document.removeEventListener('click', this.outsideHandler); } catch (_) {}
        }

        this.outsideHandler = e => {
            let inside = false;
            try {
                if (typeof e.composedPath === 'function') {
                    const path = e.composedPath();
                    inside = path.includes(this) || (this._portal && path.includes(this._portal));
                }
            } catch (_) {}

            if (!inside) {
                try {
                    const target = e.target;
                    if (this.shadowRoot?.contains(target)) inside = true;
                    else if (this.contains?.(target)) inside = true;
                    else if (this._portal?.contains(target)) inside = true;
                } catch (_) {}
            }

            if (!inside) this.closePanel();
        };

        document.addEventListener('click', this.outsideHandler);
    },

    clearShadow() {
        if (this.outsideHandler) {
            try { document.removeEventListener('click', this.outsideHandler); } catch (_) {}
            this.outsideHandler = null;
        }
        this.panel   = null;
        this.trigger = null;
        this.isOpen  = false;
        [...this.shadowRoot.children].forEach(el => {
            if (el.tagName !== 'STYLE') el.remove();
        });
    },
};